import { cloneElement, memo, ReactElement, useCallback, useMemo } from 'react'
import { executeAsync } from 'utils/performance'
import { trackInAmplitude } from '.'
import { AmplitudeEvent, InformationElement } from './amplitude.interface'
import { cloakData, FieldInteraction } from './utils'

type TooltipHandler = (isDisplayed: boolean) => void

export interface InputEventHandlers<T> {
  onChange: (value: T | undefined, raw?: string) => void
  onBlur: (value: T | undefined, raw?: string) => void
  onFocus: (value: T | undefined, raw?: string) => void
}

export interface PathComponentProps<T> extends InputEventHandlers<T> {
  onTooltip?: TooltipHandler
  path?: string
}

interface Props {
  children: ReactElement<PathComponentProps<unknown>>
  path: string
  trackOnChange?: boolean
  trackFocusEvents?: boolean
  trackTooltipClick?: boolean
}

/**
 * TODO: Refactor into hooks please! :)
 */
const WithFieldInteraction = ({
  children,
  trackOnChange = false,
  trackFocusEvents = true,
  trackTooltipClick = true,
  path
}: Props) => {
  const {
    props: { onBlur, onChange, onFocus, onTooltip, ...childProps }
  } = children

  const onBlurHandler = useCallback(
    (value: unknown, raw?: string) => {
      if (onBlur) {
        onBlur(value, raw)
      }

      if (trackFocusEvents) {
        executeAsync(() => {
          trackInAmplitude(AmplitudeEvent.InteractWithField, {
            path,
            EventName: FieldInteraction.onBlur,
            value: cloakData(raw || value, path)
          })
        })
      }
    },
    [path, onBlur, trackFocusEvents]
  )
  const onFocusHandler = useCallback(
    (value: unknown, raw?: string) => {
      if (onFocus) {
        onFocus(value, raw)
      }

      if (trackFocusEvents) {
        executeAsync(() => {
          trackInAmplitude(AmplitudeEvent.InteractWithField, {
            path,
            EventName: FieldInteraction.onFocus,
            value: cloakData(raw || value, path)
          })
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFocus, path, trackFocusEvents]
  )
  const onChangeHandler = useCallback(
    (value: unknown, raw?: string) => {
      if (onChange) {
        onChange(value, raw)
      }

      if (trackOnChange) {
        executeAsync(() => {
          trackInAmplitude(AmplitudeEvent.InteractWithField, {
            path,
            EventName: FieldInteraction.onChange,
            value: cloakData(raw !== undefined ? raw : value, path)
          })
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, path, trackOnChange]
  )
  const onTooltipHandler = useCallback(
    (isVisible: boolean) => {
      if (onTooltip) {
        onTooltip(isVisible)
      }

      if (trackTooltipClick) {
        executeAsync(() => {
          trackInAmplitude(AmplitudeEvent.ToggleInformation, {
            path,
            type: InformationElement.Tooltip,
            isVisible
          })
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onTooltip, path, trackTooltipClick]
  )

  const handlers = useMemo(
    () => ({
      onBlur: onBlurHandler,
      onFocus: onFocusHandler,
      onChange: onChangeHandler,
      ...(onTooltip ||
        (trackTooltipClick && {
          onTooltip: onTooltipHandler
        }))
    }),
    [
      onBlurHandler,
      onFocusHandler,
      onChangeHandler,
      onTooltipHandler,
      onTooltip,
      trackTooltipClick
    ]
  )

  return cloneElement(children, {
    ...childProps,
    ...handlers
  })
}

export default memo(WithFieldInteraction)
